import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { IconArrowLeft, IconBox, IconBuilding, IconCheck, IconChevronDown, IconDots, IconDownload, IconFileText, IconHistory, IconInfoCircle, IconMessageCircle, IconNotes, IconPencil, IconRoute, IconSearch, IconShieldCheck, IconSparkles, IconTag, IconUsers, IconFlag, IconArrowRight, IconExternalLink, IconGitCompare, IconShip } from '@tabler/icons-react';
import { Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { EDIT_BOOK_CNEE, EDIT_CHECKS, EDIT_FIELDS, EDIT_FIELD_DEFS, EDIT_HBL, EDIT_HIST, EDIT_JOB, EDIT_PARTIES, EDIT_PEOPLE, EDIT_STEPS, EDIT_SUGS, EDIT_SUM, EDIT_TASKS, EDIT_VER, type PartyKey } from './editSample';
import { ClauseBody, ContBlock, DocsBody, FeesBlock } from './BlEditSections';
import s from './blEdit.module.css';

// Mint-BLEdit · Soạn B/L (QĐ 25/Oct 08:10: bố cục 2:1, không thanh bước soạn) — chế độ Draft v2 của dữ liệu mẫu.
// Chỉ đọc: nút ghi báo toast "Chưa mở ghi ở bản này"; ô nhập sửa trong phiên, không lưu.

const TIP = {
  party: 'Shipper, Consignee, Notify, Delivery agent in trên B/L. Tra đối tác lấy từ danh bạ; Consignee gợi ý theo lịch sử shipper.',
  route: 'Tàu, chuyến, cảng theo booking. Sửa ở đây rồi Lưu sẽ đồng bộ về booking.',
  cargo: 'Hàng và container theo SI; số cont kiểm ISO 6346, tổng GW cont so với GW B/L.',
  terms: 'Loại vận đơn HBL / MBL, điều khoản cước theo booking.',
  clause: 'DEM / DET và ghi chú in lên B/L.',
  docs: 'Tệp gắn B/L: SI khách, booking, Draft HBL, Draft MBL.',
  ck: 'Trường bắt buộc còn thiếu, chỗ lệch so với SI của khách, booking và danh sách container. Bấm → để tới trường.',
  ap: 'Draft → gửi khách → khách duyệt → phát hành. Người duyệt revise lấy theo ghế tổ chức.',
  hi: 'Sửa, lưu phiên bản, Ask HANA đọc SI, VGM. Mỗi lần lưu / xác nhận / release đều chụp một phiên bản. Xem bản in mở trình xem tệp chung.',
} as const;

const TONE = { info: s.dB, ai: s.dV, ok: s.dG, mute: s.dM } as const;

type Mode = 'new' | 'draft' | 'confirmed' | 'released';
function readMode(): Mode {
  const v = new URLSearchParams(location.search).get('mode');
  return v === 'new' || v === 'confirmed' || v === 'released' ? v : 'draft';
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const Tip = ({ tip }: { tip: string }) => (
  <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}>
    <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
  </span>
);

function Fold({ title, open, onClick }: { title: string; open: boolean; onClick: () => void }) {
  return (
    <button type="button" className={s.fold} aria-label={`Mở hoặc gập ${title}`} title="Mở / gập" aria-expanded={open} onClick={onClick}>
      <span className={`${s.chev} ${open ? s.chevUp : ''}`}><IconChevronDown size={18} stroke={1.9} aria-hidden="true" /></span>
    </button>
  );
}

function Count({ n, warn, tip }: { n: number; warn?: boolean; tip: string }) {
  return <span className={`${s.cnt} ${warn ? s.cntW : s.cntE}`} title={tip}><i />{n}</span>;
}

/** Mục gập của cột trái: tiêu đề + tóm tắt khi gập (board: tiêu đề là chữ trong nút ẩn khỏi trình đọc, nút gập riêng). */
function Sec({ id, title, icon, tip, sum, diff, open, onToggle, extra, children }: { id: string; title: string; icon: ReactNode; tip: string; sum?: string; diff?: number; open: boolean; onToggle: () => void; extra?: ReactNode; children?: ReactNode }) {
  return (
    <section id={`bl-sec-${id}`} aria-label={title} className={s.card}>
      <div className={`${s.ch} ${s.chL}`}>
        <span className={s.ico} aria-hidden="true">{icon}</span>
        <span className={s.ttl}>{title}</span>
        <Tip tip={tip} />
        {!!diff && <Count n={diff} warn tip={`${diff} chỗ lệch SI / booking`} />}
        {open ? <span className={s.grow} /> : <span className={s.sum} title={sum}>{sum}</span>}
        {extra}
        <Fold title={title} open={open} onClick={onToggle} />
      </div>
      {open && children}
    </section>
  );
}

type Open = Record<'party' | 'route' | 'cargo' | 'terms' | 'clause' | 'docs' | 'ck' | 'ap' | 'hi', boolean>;

function Editor({ noperm, mode }: { noperm: boolean; mode: Mode }) {
  const t = useT();
  const toast = useToast();
  // Mint-BLEdit-iPad: chạm ≥ 44px, ô nhập 16px; không Tra đối tác / gợi ý / Chứng từ B/L; Trạng thái & Lịch sử gập sẵn.
  const pad = useLayout() === 'pad';
  const checks = pad ? EDIT_CHECKS.filter((c) => !c.fix) : EDIT_CHECKS;
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [vals, setVals] = useState<Record<PartyKey, string>>({ ...EDIT_PARTIES });
  const [fv, setFv] = useState<Record<string, string>>({ ...EDIT_FIELDS });
  const [upper, setUpper] = useState(true);
  const [same, setSame] = useState(true);
  const [open, setOpen] = useState<Open>({ party: true, route: false, cargo: false, terms: false, clause: false, docs: false, ck: true, ap: !pad, hi: !pad });
  const [done, setDone] = useState<Record<string, boolean>>({});
  const tog = (k: keyof Open) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const set = (k: PartyKey, v: string) => setVals((p) => ({ ...p, [k]: upper ? v.toUpperCase() : v }));
  // confirmed / released: khoá sửa (board: locked = mode confirmed | released)
  const locked = mode !== 'draft';
  const ro = noperm || locked;
  const goField = (c: (typeof EDIT_CHECKS)[number]) => {
    const el = c.k.includes('Consignee') ? document.getElementById('bl-f-cnee') : null;
    if (el) { setOpen((o) => ({ ...o, party: true })); el.scrollIntoView({ block: 'center' }); el.focus(); } else noWrite();
  };
  const parties: readonly { key: PartyKey; k: string; req?: boolean; id: string }[] = [
    { key: 'shipper', k: 'Shipper', req: true, id: 'bl-f-shipper' },
    { key: 'cnee', k: 'Consignee', req: true, id: 'bl-f-cnee' },
    { key: 'notify', k: 'Notify party', id: 'bl-f-notify' },
    { key: 'agent', k: 'Delivery agent', id: 'bl-f-agent' },
  ];
  const steps = EDIT_STEPS.map((p, i) => {
    if (mode === 'confirmed') return i === 0 ? { ...p, cur: false, done: true } : i === 1 ? { ...p, cur: true } : p;
    if (mode === 'released') return { ...p, cur: false, done: true, d: i === 0 ? p.d : '22/Oct' };
    return p;
  });
  const cneeOff = vals.cnee !== EDIT_BOOK_CNEE;
  const setF = (key: string, v: string, up?: boolean) => setFv((o) => ({ ...o, [key]: up && upper ? v.toUpperCase() : v }));
  const fields = (sec: 'route' | 'cargo' | 'terms', more?: ReactNode) => (
    <div className={s.body}>
      <div className={s.fgrid}>
        {EDIT_FIELD_DEFS[sec].map((f) => {
          const id = `bl-f-${f.key}`;
          const v = fv[f.key] ?? '';
          const dirty = v !== (EDIT_FIELDS[f.key] ?? '');
          const miss = !!f.req && v.trim() === '';
          const tone = miss ? s.finErr : dirty ? s.finDirty : '';
          return (
            <div key={f.key} className={s.fld} style={{ gridColumn: `span ${pad ? Math.min(f.span, 2) : f.span}` }}>
              <div className={s.fk}>
                <span>{f.k}{f.req && <span className={s.req} title="Bắt buộc" aria-label="bắt buộc"> *</span>}</span>
                <span className={`${s.fmark} ${dirty && !miss ? s.fmarkD : ''}`}>{miss ? 'thiếu' : dirty ? 'đã sửa' : ''}</span>
              </div>
              {f.opts ? (
                <div id={id} role="radiogroup" aria-label={f.k} className={s.fseg}>
                  {f.opts.map((o) => <button key={o} type="button" role="radio" aria-checked={v === o} disabled={ro} className={s.fsegB} onClick={() => setF(f.key, o)}>{o}</button>)}
                </div>
              ) : f.area ? (
                <textarea id={id} aria-label={f.k} className={`${s.ta} ${tone}`} readOnly={ro} value={v} onChange={(e) => setF(f.key, e.target.value, f.up)} />
              ) : (
                <input id={id} aria-label={f.k} className={`${s.fin} ${tone}`} readOnly={ro} value={v} onChange={(e) => setF(f.key, e.target.value, f.up)} />
              )}
            </div>
          );
        })}
      </div>
      {more}
    </div>
  );

  return (
    <section aria-label={pad ? 'Form B/L' : 'Soạn B/L'} className={`${s.grid} ${pad ? s.pad : ''}`}>
      <div className={s.col}>
        <Sec id="party" title="Các bên" icon={<IconUsers size={18} stroke={1.9} />} tip={TIP.party} diff={2} sum={`${vals.shipper.split('\n')[0]} → ${vals.cnee.split('\n')[0]}`} open={open.party} onToggle={() => tog('party')}
          extra={<label className={s.up}><input type="checkbox" checked={upper} disabled={ro} onChange={() => setUpper(!upper)} />{t('bl.autoUpper')}</label>}>
          <div className={s.body}>
            <div className={s.parties}>
              {parties.map((p) => {
                const readOnly = ro || (p.key === 'notify' && same);
                return (
                  <div key={p.key} className={s.party}>
                    <div className={s.pl}>
                      <span className={s.pk}>{p.k}{p.req && <span className={s.req} title="Bắt buộc" aria-label="bắt buộc"> *</span>}</span>
                      {!pad && <button type="button" className={s.lbl} aria-label="Tra đối tác" title="Tra đối tác" disabled={readOnly} onClick={noWrite}><IconSearch size={14} stroke={1.8} aria-hidden="true" /></button>}
                    </div>
                    <textarea id={p.id} className={s.ta} aria-label={p.k} readOnly={readOnly} value={p.key === 'notify' && same ? vals.notify : vals[p.key]} onChange={(e) => set(p.key, e.target.value)} />
                    {p.key === 'notify' && <label className={s.same}><input type="checkbox" checked={same} disabled={ro} onChange={() => setSame(!same)} />Same as consignee</label>}
                    {p.key === 'cnee' && !noperm && !pad && (
                      <>
                        {!locked && <div className={s.sugs}>
                          <span title="Gợi ý theo lịch sử shipper" style={{ display: 'inline-flex' }}><IconHistory size={14} stroke={1.9} aria-hidden="true" /></span>
                          {EDIT_SUGS.map(([k, v]) => <button key={k} type="button" className={s.sug} onClick={() => set('cnee', v)}>{k}</button>)}
                        </div>}
                        {cneeOff && (
                          <div role="note" className={s.warn}>
                            <i />
                            <span title={`Khác booking: ${EDIT_BOOK_CNEE.replace(/\n/g, ' ')}`}>{`Khác booking: ${EDIT_BOOK_CNEE.split('\n')[1]}`}</span>
                            <button type="button" className={`${s.btn} ${s.btnW}`} disabled={locked} onClick={() => set('cnee', EDIT_BOOK_CNEE)}><IconDownload size={14} stroke={1.8} aria-hidden="true" />{t('bl.fromBooking')}</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Sec>
        <Sec id="route" title="Tuyến & tàu" icon={<IconRoute size={18} stroke={1.9} />} tip={TIP.route} sum={EDIT_SUM.route} open={open.route} onToggle={() => tog('route')}>{fields('route')}</Sec>
        <Sec id="cargo" title="Hàng & container" icon={<IconBox size={18} stroke={1.9} />} tip={TIP.cargo} diff={2} sum={EDIT_SUM.cargo} open={open.cargo} onToggle={() => tog('cargo')}>{fields('cargo', <ContBlock ro={ro} unit={fv.unit ?? 'BAGS'} />)}</Sec>
        <Sec id="terms" title="Điều khoản & cước" icon={<IconFileText size={18} stroke={1.9} />} tip={TIP.terms} sum={EDIT_SUM.terms} open={open.terms} onToggle={() => tog('terms')}>{fields('terms', <FeesBlock ro={ro} />)}</Sec>
        <Sec id="clause" title="Ghi chú / clause" icon={<IconPencil size={18} stroke={1.9} />} tip={TIP.clause} sum={EDIT_SUM.clause} open={open.clause} onToggle={() => tog('clause')}><ClauseBody ro={ro} /></Sec>
        {!pad && <Sec id="docs" title="Chứng từ B/L" icon={<IconNotes size={18} stroke={1.9} />} tip={TIP.docs} sum={EDIT_SUM.docs} open={open.docs} onToggle={() => tog('docs')}><DocsBody ro={ro} /></Sec>}
      </div>
      <aside aria-label="Kiểm tra và duyệt" className={s.col}>
        {ro && <div role="note" className={s.note}><IconShieldCheck size={16} aria-hidden="true" /><span>{t('bl.viewOnlyJob')}</span></div>}
        <section aria-label="Kiểm tra" className={s.card}>
          <div className={s.ch}>
            <span className={s.ico} aria-hidden="true"><IconShieldCheck size={18} stroke={1.9} /></span>
            <h2 className={s.ttl}>{t('bl.validate')}</h2>
            <Tip tip={TIP.ck} />
            <span className={s.grow} />
            <Count n={checks.length} warn tip="Chỗ lệch so với SI / booking / container" />
            <Fold title="Kiểm tra" open={open.ck} onClick={() => tog('ck')} />
          </div>
          {open.ck && (
            <div className={s.cb}>
              {checks.map((c) => (
                <div key={c.k} className={s.row}>
                  <span className={`${s.dot} ${c.kind === 'miss' ? s.dE : s.dW}`} aria-hidden="true" />
                  <span className={s.rk} title={c.tip}>{c.k}</span>
                  <span className={s.rs}>{c.src}</span>
                  {c.fix && !ro && <button type="button" className={`${s.btn} ${s.btnS}`} onClick={() => set('cnee', EDIT_BOOK_CNEE)}><IconDownload size={14} stroke={1.8} aria-hidden="true" />{c.fix}</button>}
                  <button type="button" className={s.sq} aria-label={`Tới trường: ${c.k}`} title="Tới trường" onClick={() => goField(c)}><IconArrowRight size={15} stroke={1.9} aria-hidden="true" /></button>
                </div>
              ))}
              <div className={s.acts}>
                {!locked && <button type="button" className={`${s.btn} ${s.btnP}`} onClick={noWrite}><IconSparkles size={15} stroke={1.9} aria-hidden="true" />{t('bl.matchSi')}</button>}
                <button type="button" className={s.btn} disabled={ro} onClick={noWrite}><IconMessageCircle size={16} stroke={1.8} aria-hidden="true" />{t('bl.askCustomer')}</button>
              </div>
            </div>
          )}
        </section>
        <section aria-label="Trạng thái & duyệt" className={s.card}>
          <div className={s.ch}>
            <span className={s.ico} aria-hidden="true"><IconFlag size={18} stroke={1.9} /></span>
            <h2 className={s.ttl}>{t('bl.statusApprove')}</h2>
            <Tip tip={TIP.ap} />
            <span className={s.grow} />
            <Fold title="Trạng thái & duyệt" open={open.ap} onClick={() => tog('ap')} />
          </div>
          {open.ap && (
            <div className={s.cb}>
              {locked && !noperm && (
                <div role="note" className={s.note} title={mode === 'confirmed' ? 'Cần sửa thì Revise B/L: B/L quay về Draft và lưu phiên bản mới.' : 'HBL đã release cho khách. Revise sau release cần Trưởng CS duyệt, sửa xong gửi lại khách.'}>
                  <IconShieldCheck size={16} aria-hidden="true" />
                  <span>{mode === 'confirmed' ? 'Đã xác nhận · khoá sửa' : 'Đã release · khoá sửa'}</span>
                  <button type="button" className={s.btn} onClick={noWrite}>{mode === 'confirmed' ? 'Revise B/L' : 'Gửi duyệt revise'}</button>
                </div>
              )}
              <div role="list" aria-label="Luồng duyệt B/L" className={s.steps}>
                {steps.map((p, i) => (
                  <div key={p.k} role="listitem" className={s.step} title={p.k + (p.cur ? ' · đang ở bước này' : '')}>
                    {i < EDIT_STEPS.length - 1 && <span className={s.ln} aria-hidden="true" />}
                    <span className={`${s.mk} ${p.cur ? s.mkCur : ''}`}>{p.done ? <IconCheck size={14} stroke={3} aria-hidden="true" /> : i + 1}</span>
                    <span className={`${s.sk} ${p.cur ? s.skCur : ''}`}>{p.k}</span>
                    <span className={s.sd}>{p.d}</span>
                  </div>
                ))}
              </div>
              {EDIT_PEOPLE.map((p) => (
                <div key={p.u} className={s.row}>
                  <span className={s.av} title={p.u}>{p.u.slice(0, 2)}</span>
                  <span className={s.pn} title={p.name}>{p.name}</span>
                  <span className={s.rs}>{p.role}</span>
                </div>
              ))}
              <div className={s.tk0}>{t('bl.docTasks')}</div>
              {EDIT_TASKS.map((t) => (
                <label key={t.code} className={`${s.tk} ${done[t.code] ? s.tkDone : ''}`}>
                  <input type="checkbox" aria-label={`Xong: ${t.k}`} checked={!!done[t.code]} disabled={ro} onChange={() => setDone((d) => ({ ...d, [t.code]: !d[t.code] }))} />
                  <span className={s.code}>{t.code}</span>
                  <span className={s.rk} title={t.k}>{t.k}</span>
                  <span className={`${s.rs} ${t.late && !done[t.code] ? s.late : ''}`} title="Hạn">{t.due}</span>
                </label>
              ))}
            </div>
          )}
        </section>
        <section aria-label="Lịch sử" className={s.card}>
          <div className={s.ch}>
            <span className={s.ico} aria-hidden="true"><IconHistory size={18} stroke={1.9} /></span>
            <h2 className={s.ttl}>{t('bl.history')}</h2>
            <Tip tip={TIP.hi} />
            <span className={s.grow} />
            <button type="button" className={s.sq} aria-label="Xem bản in" title="Mở bản in HBL trong trình xem tệp (FilePreview)" onClick={noWrite}><IconExternalLink size={14} stroke={1.9} aria-hidden="true" /></button>
            <Fold title="Lịch sử" open={open.hi} onClick={() => tog('hi')} />
          </div>
          {open.hi && (
            <div className={s.cb}>
              {EDIT_HIST.map((h) => (
                <div key={h.k} className={`${s.row} ${s.hrow}`}>
                  <span className={`${s.dot} ${TONE[h.tone]}`} aria-hidden="true" />
                  <span className={s.rk} title={h.k}>{h.k}</span>
                  <span className={s.rs}>{h.when}</span>
                </div>
              ))}
              <div className={s.acts}>
                <button type="button" className={s.btn} disabled={EDIT_VER < 2} onClick={noWrite}><IconGitCompare size={16} stroke={1.8} aria-hidden="true" />{t('bl.compare')}</button>
                <button type="button" className={s.btn} onClick={noWrite}><IconExternalLink size={14} stroke={1.9} aria-hidden="true" />Draft MBL</button>
              </div>
            </div>
          )}
        </section>
      </aside>
    </section>
  );
}

const NEW_ROWS = [
  { k: 'Loại B/L', opts: [['HBL', 'HBL (HANA phát hành)'], ['MBL', 'Chỉ MBL của hãng']], hint: '' },
  { k: 'Loại bill', opts: [['Original', 'Original'], ['Surrender', 'Surrender'], ['Seaway', 'Seaway']], hint: 'Đổi được sau khi tạo. Đổi sau khi đã gửi Draft thì Draft cũ hết hiệu lực.' },
  { k: 'Điền dữ liệu', opts: [['book', 'Lấy từ booking'], ['si', 'Đọc SI bằng Ask HANA']], hint: '' },
] as const;

/** Tạo B/L (job chưa có B/L) — board: mode=new hoặc state=empty. */
function CreateForm({ noperm, onCreate }: { noperm: boolean; onCreate: () => void }) {
  const t = useT();
  const [pick, setPick] = useState<string[]>(['HBL', 'Surrender', 'book']);
  const issue = pick[0];
  return (
    <section aria-label={t('bledit.create')} className={s.create}>
      <div className={s.createCard}>
        <div>
          <h2 className={s.createTtl}><IconBox size={16} stroke={1.8} aria-hidden="true" />{t('bledit.noBl')}</h2>
          <div className={s.createSub}>{t('bledit.noBlSub')}</div>
        </div>
        {NEW_ROWS.map((r, ri) => (
          <div key={r.k}>
            <div className={s.createK}>{r.k}</div>
            <div role="radiogroup" aria-label={r.k} className={s.seg}>
              {r.opts.map(([key, label]) => (
                <button key={key} type="button" role="radio" aria-checked={pick[ri] === key} className={`${s.segB} ${pick[ri] === key ? s.segOn : ''}`} onClick={() => setPick(pick.map((v, i) => (i === ri ? key : v)))}>{label}</button>
              ))}
            </div>
            {r.hint && <div className={s.createHint}>{r.hint}</div>}
          </div>
        ))}
        <div className={s.createNo}>
          <div><div className={s.createHint0}>{`Số ${issue} cấp tự động`}</div><b>{issue === 'HBL' ? EDIT_HBL : 'theo số MBL của hãng'}</b></div>
          <div><div className={s.createHint0}>Theo booking</div><b>{`${EDIT_JOB.vsl} · ${EDIT_JOB.vol}`}</b></div>
        </div>
        <div><Button variant="primary" disabled={noperm} onClick={onCreate}>{t('bledit.create')}</Button></div>
      </div>
    </section>
  );
}

export function BlEditPage() {
  const t = useT();
  const mode = readMode();
  const toast = useToast();
  const navigate = useNavigate();
  const rawState: ScreenState = readState() ?? 'ready';
  const state: ScreenState = rawState === 'empty' ? 'ready' : rawState;
  const isNew = mode === 'new' || rawState === 'empty';
  const back = () => void navigate({ to: '/ezy/bl' });
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const ready = state === 'ready' || state === 'noperm';
  const isPad = useLayout() === 'pad';
  return (
    <Page pad={isPad ? undefined : 32}>
      <PageHeader
        dense={!isPad}
        noFind={!isPad}
        title={isNew && ready ? `Tạo B/L · ${EDIT_JOB.id}` : EDIT_HBL}
        findHint={false}
        lead={<a className={s.back} href="/ezy/bl" aria-label="Về Chứng từ & B/L" title="Về Chứng từ & B/L" onClick={(e) => { e.preventDefault(); back(); }}><IconArrowLeft size={18} stroke={1.9} aria-hidden="true" /></a>}
        meta={ready && isNew ? (
          <span className={s.head}><span className={s.pill}><i style={{ background: 'var(--d-gray)' }} />{t('bledit.noBlChip')}</span></span>
        ) : ready ? (
          <span className={s.head}>
            <span className={s.pill} title="BLStatus"><i style={{ background: mode === 'draft' ? 'var(--d-gray)' : 'var(--d-blue)' }} />{mode === 'draft' ? 'Draft' : 'Confirmed'}</span>
            <span className={s.pill} title="HBLStatus"><i style={{ background: mode === 'released' ? 'var(--d-green)' : 'var(--d-amber)' }} />{mode === 'released' ? 'HBL Released' : 'HBL Hold'}</span>
            <span className={s.pill} title="MBLStatus"><i style={{ background: mode === 'released' ? 'var(--d-green)' : 'var(--d-blue)' }} />{mode === 'released' ? 'MBL Released' : 'MBL Wait'}</span>
            <button type="button" className={`${s.pill} ${s.pillV}`} title="Loại vận đơn (HBL) · bấm để sửa ở Điều khoản & cước" onClick={noWrite}><i style={{ background: 'var(--d-violet)' }} />Surrender</button>
          </span>
        ) : undefined}
        sub={ready ? (
          <span className={s.subl}>
            <Link to="/ezy/jobs/$jobId" params={{ jobId: EDIT_JOB.id }}>{EDIT_JOB.id}</Link>
            <span title="Khách hàng"><IconBuilding size={14} stroke={1.8} aria-hidden="true" />{EDIT_JOB.cus}</span>
            <span title="Tuyến"><IconRoute size={14} stroke={1.8} aria-hidden="true" />{EDIT_JOB.route}</span>
            <span title="Tàu · hãng"><IconShip size={14} stroke={1.8} aria-hidden="true" />{EDIT_JOB.vsl}</span>
            <span title="Khối lượng"><IconBox size={14} stroke={1.8} aria-hidden="true" />{EDIT_JOB.vol}</span>
            <span title="Mã dịch vụ"><IconTag size={14} stroke={1.8} aria-hidden="true" />{EDIT_JOB.svc}</span>
            <span className={s.ver} title="Phiên bản B/L">{isNew ? 'chưa có phiên bản' : `v${EDIT_VER}`}</span>
          </span>
        ) : undefined}
        actions={ready ? (
          <>
            <button type="button" className={s.more} aria-label="Thao tác khác" title="Thao tác khác" aria-haspopup="menu" onClick={noWrite}><IconDots size={18} stroke={1.9} aria-hidden="true" /></button>
            {!isNew && <Button variant="primary" disabled={state === 'noperm' && mode === 'draft'} onClick={noWrite}>{mode === 'draft' ? 'Xác nhận B/L' : mode === 'confirmed' ? 'Gửi khách duyệt' : 'In B/L'}</Button>}
          </>
        ) : undefined}
      />
      <div className={s.page}>
        {ready && isNew ? <CreateForm noperm={state === 'noperm'} onCreate={noWrite} /> : ready ? <Editor noperm={state === 'noperm'} mode={mode === 'new' ? 'draft' : mode} /> : (
          <StateView
            state={state}
            icon={<IconFileText size={26} stroke={1.8} />}
                        action={state === 'error' ? <Button variant="secondary" onClick={() => location.reload()}>{t('bl.retry')}</Button>: undefined}
            skeleton={<div className={s.grid}><div style={{ height: 460, borderRadius: 16, overflow: 'hidden' }}><Skeleton /></div><div style={{ height: 540, borderRadius: 16, overflow: 'hidden' }}><Skeleton /></div></div>}
          >
            {null}
          </StateView>
        )}
      </div>
    </Page>
  );
}
